/**
 * A single finding, framed as what it is: a risk, an opening, or a strength.
 *
 * `rootCause` is set apart rather than run into the finding — the observation
 * and the explanation for it are different claims, and the second is the one a
 * reader will argue with.
 */

import React from "react";
import PropTypes from "prop-types";
import { FiAlertTriangle, FiCompass, FiShield } from "react-icons/fi";

const KINDS = {
  warning: { Icon: FiAlertTriangle, label: "Watch" },
  opportunity: { Icon: FiCompass, label: "Opportunity" },
  strength: { Icon: FiShield, label: "Strength" },
};

const DiagnosticCard = ({
  type = "warning",
  title,
  subtitle,
  metric,
  benchmark,
  finding,
  rootCause,
}) => {
  const { Icon, label } = KINDS[type] ?? KINDS.warning;
  return (
    <section className={`lou-diag lou-diag--${type}`}>
      <header className="lou-diag__head">
        <span className="lou-diag__icon" aria-hidden="true">
          <Icon size={16} />
        </span>
        <div className="lou-diag__heading">
          <h4 className="lou-diag__title">{title}</h4>
          {subtitle ? <span className="lou-diag__subtitle">{subtitle}</span> : null}
        </div>
        <span className="lou-diag__kind">{label}</span>
      </header>

      {metric || benchmark ? (
        <div className="lou-diag__stats">
          {metric ? <span className="lou-diag__metric">{metric}</span> : null}
          {benchmark ? (
            <span className="lou-diag__benchmark">{benchmark}</span>
          ) : null}
        </div>
      ) : null}

      {finding ? <p className="lou-diag__finding">{finding}</p> : null}

      {rootCause ? (
        <div className="lou-diag__cause">
          <span className="lou-diag__causeLabel">Likely cause</span>
          <p className="lou-diag__causeText">{rootCause}</p>
        </div>
      ) : null}
    </section>
  );
};

DiagnosticCard.propTypes = {
  type: PropTypes.oneOf(["warning", "opportunity", "strength"]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  metric: PropTypes.string,
  benchmark: PropTypes.string,
  finding: PropTypes.string,
  rootCause: PropTypes.string,
};

export default DiagnosticCard;
